/**
 * Step 3 of the create-alias wizard: the passphrase.
 *
 * Two deliberate asymmetries:
 *
 * - Only the first field has a reveal toggle. The confirmation exists to catch
 *   typos, which it cannot do if the user can read it back.
 * - The examples popover is opened by a labelled link above the field rather
 *   than an unlabelled icon beside it, so its purpose is obvious before the user
 *   has committed to a password.
 */

import { useTranslation } from 'react-i18next';
import { Alert } from '../../components/Alert';
import { Input } from '../../components/Input';
import { Popover } from '../../components/Popover';
import { Icon } from '../../icons/Icon';
import {
  MIN_PASSPHRASE_LENGTH,
  getPassphraseStrength,
  stringArrayFromI18nReturn,
} from '../identityModalUtils';

export interface CreatePasswordStepProps {
  passphrase: string;
  onPassphraseChange: (value: string) => void;
  passphraseConfirm: string;
  onPassphraseConfirmChange: (value: string) => void;
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  disabled?: boolean;
}

export function CreatePasswordStep({
  passphrase,
  onPassphraseChange,
  passphraseConfirm,
  onPassphraseConfirmChange,
  visible,
  onVisibleChange,
  disabled,
}: CreatePasswordStepProps) {
  const { t } = useTranslation();

  // Empty fields "match" vacuously; only surface the status once something is typed.
  const mismatch = passphraseConfirm.length > 0 && passphrase !== passphraseConfirm;
  const matches = passphrase.length > 0 && passphrase === passphraseConfirm;
  const longEnough = passphrase.length >= MIN_PASSPHRASE_LENGTH;
  const strength = matches && longEnough ? getPassphraseStrength(passphrase) : null;

  return (
    <div className="identity-wizard-step">
      {/* Belongs on this step, not the whole wizard: it is only actionable once
          there is a password to write down. */}
      <Alert variant="warning">{t('identity.create.noRecoveryWarning')}</Alert>

      <div className="identity-password-examples">
        <Popover
          trigger={
            <button type="button" className="identity-hints-link">
              {t('identity.create.passwordExamplesLink')}
            </button>
          }
          positioning={{ placement: 'bottom-start' }}
          className="passphrase-popover"
        >
          <div className="passphrase-info-content">
            <h4>{t('identity.create.passwordExamplesTitle')}</h4>
            <ul>
              {stringArrayFromI18nReturn(
                t('identity.create.passwordExamples', { returnObjects: true }),
              ).map((example, i) => (
                <li key={i}>{example}</li>
              ))}
            </ul>
            <p className="passphrase-tip">{t('identity.create.passwordExamplesTip')}</p>
          </div>
        </Popover>
      </div>

      <Input
        type={visible ? 'text' : 'password'}
        placeholder={t('identity.create.passwordPlaceholder')}
        value={passphrase}
        onChange={(e) => onPassphraseChange(e.target.value)}
        disabled={disabled}
        autoFocus
        autoComplete="new-password"
        hint={t('identity.create.passwordHint')}
        rightIcon={
          <button
            type="button"
            className="input-password-toggle"
            onClick={() => onVisibleChange(!visible)}
            disabled={disabled}
            aria-label={
              visible
                ? t('identity.passwordVisibility.hide')
                : t('identity.passwordVisibility.show')
            }
            aria-pressed={visible}
          >
            <Icon name={visible ? 'eyeSlash' : 'eye'} />
          </button>
        }
      />

      <Input
        type="password"
        placeholder={t('identity.create.passwordConfirmPlaceholder')}
        value={passphraseConfirm}
        onChange={(e) => onPassphraseConfirmChange(e.target.value)}
        disabled={disabled}
        autoComplete="new-password"
        // Border/aria only — the copy lives in the shared status line below so
        // mismatch and match share the same spacing as each other.
        className={mismatch ? 'input-error' : undefined}
        aria-invalid={mismatch || undefined}
      />

      {mismatch && (
        <p className="passphrase-strength passphrase-strength-weak" role="alert">
          <span className="passphrase-strength-match">
            {t('identity.create.passwordMismatch')}
          </span>
        </p>
      )}

      {matches && (
        <p
          className={`passphrase-strength passphrase-strength-${
            longEnough && strength ? strength : 'weak'
          }`}
        >
          <span className="passphrase-strength-match">
            {longEnough
              ? t('identity.create.passwordStrength.match')
              : t('identity.create.passwordStrength.tooShort', {
                  min: MIN_PASSPHRASE_LENGTH,
                })}
          </span>
          {longEnough && strength && (
            <span className="passphrase-strength-hint">
              &mdash; {t(`identity.create.passwordStrength.${strength}`)}
            </span>
          )}
        </p>
      )}
    </div>
  );
}
